import afterFrame from "afterframe";
import {useCallback, useRef} from "react";

import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {emitGuideAudioStream, emitGuideCommand} from "@khan/events-guide";
import {
    AudioCache,
    getNotificationVariable,
    maybeGetAsStudentPersona,
    setPromptVariables,
} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";
import {
    ActionStatusStreamAction,
    ActionStatusStreamStatus,
    type ActionStatusMap,
    type AIGuideMetadata,
    type AIGuideStreamHandlers,
    type Message,
    type MessageAudioChunk,
} from "@khan/guide-types";
import Log, {type KAError} from "@khan/logging";
import {useUserAttributes} from "@khan/shared-core";
import {useLocation} from "@khan/urls";
import {useDocumentVisibilityRef} from "@khan/util-document-visibility";

import {useGuideAudioController} from "../hooks/use-guide-audio-controller.ts";
import {useGuidePreferences} from "../hooks/use-guide-preferences.ts";
import {formatUsageQuotaLowWarning} from "../strings.ts";
import {getLastIncomingMessageById} from "../utils/get-last-incoming-message-by-id.ts";

import {appendConversationWithErrorMessage} from "./append-conversation-with-error-message.ts";
import {LOW_QUOTA_THRESHOLD} from "./constants.ts";
import {updateConversationForTeacherPersona} from "./update-conversation-for-teacher-persona.ts";
import {updateConversationWithStreamedAnswer} from "./update-conversation-with-streamed-answer.ts";
import {useDistrictId} from "./use-district-id.ts";
import {loadAudioSample} from "./use-guide-audio-playback.ts";
import {isChromebook} from "./utils/is-chromebook.ts";

/**
 * Get a function for generating handlers for the streaming chat API.
 *
 * @returns A function that takes an `AbortSignal`, and returns a set of
 * handlers for the streaming chat API that will update the guide state as
 * appropriate and invoke the relevant callbacks, if provided.
 */
export const useMakeStreamingHandlers = (
    reactRenderSampleRate: number,
): ((
    signal: AbortSignal,
    onResponseComplete?:
        | null
        | ((
              metadata: AIGuideMetadata,
              fullAnswer: string,
              answerContainsAudio: boolean,
          ) => void),
) => AIGuideStreamHandlers) => {
    const guideStateUpdateInProgress = useRef(false);
    // We listen for the tab visibility change to determine when the tab was
    // last hidden AKA idled.  If we have any in-flight perf metrics that
    // overlap this visibility transition, we discard them.
    // We do this because the user idling the tab makes the real-time metrics
    // we're trying to capture less useful.
    // We use visibility change as a proxy for tab becoming inactive.
    // We can't depend on performance metrics when the tab is inactive.
    const tabVisiblityRef = useDocumentVisibilityRef();

    const {pathname} = useLocation();
    const userAttributes = useUserAttributes();

    const districtId = useDistrictId();

    const {selectedVoice, audioCache, setAudioCache} =
        useGuideAudioController();
    const {preferences} = useGuidePreferences();

    const makeHandlers = useCallback(
        (
            signal: AbortSignal,
            onResponseComplete?:
                | null
                | ((
                      metadata: AIGuideMetadata,
                      fullAnswer: string,
                      answerContainsAudio: boolean,
                  ) => void),
        ) => {
            let interactionId = "";
            let mostRecentAnswer = "";
            let isAudioErrorPresent = false;
            let playback: Array<MessageAudioChunk> = [];
            const actionStatusMap: ActionStatusMap = new Map();

            const updateGuideStateWithStreamedData = () => {
                guideStateUpdateInProgress.current = true;
                // Temporarily modify the current conversation and append
                // the streamed response as it is coming in. We later
                // replace the entire conversation with the thread that
                // is returned by the server.
                GuideStateApi.set("conversation", (conversation) => {
                    afterFrame(() => {
                        guideStateUpdateInProgress.current = false;
                    });
                    // This function will return the same value if there
                    // was no need to update the conversation.
                    return updateConversationWithStreamedAnswer({
                        answer: mostRecentAnswer,
                        conversation,
                        // interactionId is set by the onStopId which is the
                        // first stream chunk that we should receive.
                        interactionId,
                        actionStatusMap: new Map(actionStatusMap),
                    });
                });
            };

            // Updates the last conversation entry having interactionIdToUpdate,
            // if it exists, with the newMessageKeyValuePairs
            const updateConversationByInteractionId = (
                interactionIdToUpdate: string,
                newMessageKeyValuePairs: Partial<Message>,
            ) => {
                GuideStateApi.set("conversation", (oldConversation) => {
                    const interactionIndex = oldConversation.findLastIndex(
                        (message) =>
                            message.interactionId === interactionIdToUpdate &&
                            message.type === "incoming",
                    );

                    // If there isn't a message yet with the given interactionId,
                    // then the message hasn't started streaming yet, so no
                    // changes can be made
                    if (interactionIndex === -1) {
                        return oldConversation;
                    }

                    // Otherwise, the message is currently streaming, or has finished
                    // streaming. In this case, update the appropriate message with
                    // the new newMessageKeyValuePairs
                    return [
                        ...oldConversation.slice(0, interactionIndex),
                        {
                            ...oldConversation[interactionIndex],
                            ...newMessageKeyValuePairs,
                        },
                        ...oldConversation.slice(interactionIndex + 1),
                    ];
                });
            };

            const onAnswer = (answer: string, networkDuration: number) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }

                mostRecentAnswer = answer;
                actionStatusMap.set(
                    ActionStatusStreamAction.TEXT,
                    ActionStatusStreamStatus.LOADING,
                );

                // If we're on a low-powered device, don't update the guide
                // state if there's already an update in progress.
                if (!isChromebook() || !guideStateUpdateInProgress.current) {
                    updateGuideStateWithStreamedData();
                }

                if (isChromebook()) {
                    const baseSampleRate = reactRenderSampleRate / 100;
                    const start = performance.now();
                    // We sample at a greater rate for this event because we want
                    // to analyze the data based on district ID and we don't want
                    // the sample size to be too small.
                    if (Math.random() < 10 * baseSampleRate) {
                        const conversation =
                            GuideStateApi.latest().conversation;
                        afterFrame((end) => {
                            if (!tabVisiblityRef.current) {
                                return;
                            }
                            // If the tab was hidden AKA idled during the perf
                            // measurement, we discard the data.
                            if (
                                !tabVisiblityRef.current.isVisible ||
                                start < tabVisiblityRef.current.visibleSince
                            ) {
                                Log.log(
                                    "tab was idled, discarding AiGuideOnAnswerPerformanceCaptured",
                                );
                                return;
                            }
                            publishAnalyticsEventForMinorConversion({
                                eventType: "AiGuideOnAnswerPerformanceCaptured",
                                eventSchemaVersion: 2,
                                numFetchMilliseconds: networkDuration,
                                numRenderMilliseconds: Math.min(
                                    end - start,
                                    60000,
                                ),
                                numMessagesInChat: conversation.length,
                                containsMath:
                                    answer.includes("\\(") &&
                                    answer.includes("\\)"),
                                districtId: districtId,
                            });
                        });
                    }

                    if (Math.random() < baseSampleRate) {
                        afterFrame((end) => {
                            if (!tabVisiblityRef.current) {
                                return;
                            }
                            // If the tab was hidden AKA idled during the perf
                            // measurement, we discard the data.s
                            if (
                                !tabVisiblityRef.current.isVisible ||
                                start < tabVisiblityRef.current.visibleSince
                            ) {
                                Log.log(
                                    "tab was idled, discarding ClientReactRenderPerformanceCaptured",
                                );
                                return;
                            }
                            publishAnalyticsEventForMinorConversion({
                                eventType:
                                    "ClientReactRenderPerformanceCaptured",
                                component: "useMakeStreamingHandlers",
                                eventSchemaVersion: 1,
                                device: "chromebook",
                                duration: Math.min(end - start, 60000),
                                interaction: "onAnswer",
                                owner: "Khanmigo Core Platform",
                            });
                        });
                    }
                }
            };

            const onAudio = (data: MessageAudioChunk | {error: KAError}) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }

                // We stop emitting audio events if we previously encountered
                // an audio error for this request.
                if (isAudioErrorPresent) {
                    return;
                }

                if ("error" in data) {
                    isAudioErrorPresent = true;

                    const errorSampleId = selectedVoice
                        ? `${selectedVoice}:${preferences.guideLanguageCode}`
                        : "lmnt:khanmigo-m-1:en";

                    actionStatusMap.set(
                        ActionStatusStreamAction.AUDIO,
                        ActionStatusStreamStatus.ERROR,
                    );

                    const errorSample = audioCache.get(
                        `${errorSampleId}:error`,
                    );
                    if (!errorSample) {
                        loadAudioSample(errorSampleId, "error").then(
                            (errorSample) => {
                                // If we encounter an audio error, we replace the playback
                                // with the error audio sample. This makes it so that if
                                // the message is replayed, only the error audio will be
                                // replayed.
                                playback = [errorSample];
                                // We also emit the error audio sample for the audio stream;
                                // marking it as the initialChunk will replace the current
                                // stream buffer with a new one containing only the error
                                // audio.
                                emitGuideAudioStream({
                                    ...errorSample,
                                    isInitialChunk: true,
                                });
                            },
                        );
                    } else {
                        playback = [errorSample[0]];
                        emitGuideAudioStream({
                            ...errorSample[0],
                            isInitialChunk: true,
                        });
                    }
                    return;
                }

                actionStatusMap.set(
                    ActionStatusStreamAction.AUDIO,
                    ActionStatusStreamStatus.LOADING,
                );

                const isInitialChunk = playback.length === 0;
                const audioChunk = data;
                playback.push(audioChunk);
                emitGuideAudioStream({...audioChunk, isInitialChunk});
            };

            const onCommand = (name: string, content: string) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }

                emitGuideCommand({name, content});
            };

            const onMetadata = (metadata: AIGuideMetadata) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }

                const {
                    interactionId,
                    threadId: handledThreadId,
                    conversation: incomingConversation,
                    conversationEntries,
                    remainingQuota,
                    restoredPromptVariables,
                } = metadata;

                // If we're receiving a response for a different thread
                // from the one we've already started, then do nothing.
                if (
                    GuideStateApi.latest().threadId &&
                    handledThreadId !== GuideStateApi.latest().threadId
                ) {
                    return;
                }

                // When we navigate, we always set the threadID to null.
                // So if a response comes back, and we currently have an
                // empty threadID, then we should update it and publish an
                // event.
                if (!GuideStateApi.latest().threadId && handledThreadId) {
                    GuideStateApi.set("threadId", handledThreadId);

                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideNewThreadStarted",
                        eventSchemaVersion: 4,
                        aiGuideThreadId: handledThreadId,
                        aiGuideNotificationId:
                            getNotificationVariable("notificationID") ?? "",
                        aiGuideNavigationId:
                            GuideStateApi.latest().navigation.id,
                    });
                }

                if (restoredPromptVariables) {
                    setPromptVariables(restoredPromptVariables);
                }

                if (incomingConversation !== undefined) {
                    // It's currently possible for there to be multiple
                    // in-flight requests to update the conversation thread (e.g.
                    // chat requests, dynamic action bubble generation, etc).
                    // To avoid overwritting conversation state with stale data
                    // we only update the impacted interaction if we detect the
                    // incoming conversation is stale, otherwise we replace the
                    // entire conversation with the incoming conversation.
                    const incomingMessage = getLastIncomingMessageById(
                        GuideStateApi.latest().conversation,
                        interactionId,
                    );
                    // If the update is for an interaction that is no longer
                    // streaming a response, it's an indicator that the incoming
                    // conversation may be stale. We only update that interaction
                    // rather than replacing the entire conversation.
                    if (
                        incomingMessage &&
                        !incomingMessage.isGeneratingResponse
                    ) {
                        const updatedIncomingMessage =
                            getLastIncomingMessageById(
                                incomingConversation,
                                interactionId,
                            );
                        // It is expected that there exists a message with the
                        // incoming interaction ID within the incoming conversation
                        // metadata. In practice, this should never be falsey.
                        if (updatedIncomingMessage) {
                            updateConversationByInteractionId(
                                interactionId,
                                updatedIncomingMessage,
                            );
                        }
                    } else {
                        GuideStateApi.set("conversation", incomingConversation);
                    }
                }

                if (conversationEntries !== undefined) {
                    GuideStateApi.set(
                        "conversationEntries",
                        conversationEntries,
                    );
                }

                // If we have a remainingQuota value (we should, but just in
                // case we don't, we guard it anyway), update that in the state
                // and check for low quota, updating the conversation
                // accordingly.
                if (remainingQuota) {
                    const remainingQuotaFloat = parseFloat(remainingQuota);
                    GuideStateApi.set("remainingQuota", remainingQuotaFloat);

                    if (remainingQuotaFloat <= LOW_QUOTA_THRESHOLD) {
                        // If the remaining quota is low, add a warning message
                        // after the guide's normal response.
                        GuideStateApi.set("conversation", (conversation) =>
                            appendConversationWithErrorMessage(
                                conversation,
                                formatUsageQuotaLowWarning(remainingQuotaFloat),
                            ),
                        );

                        publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideBotErrorMessageReceived",
                            eventSchemaVersion: 2,
                            aiGuideThreadId: handledThreadId,
                            aiGuideNavigationId:
                                GuideStateApi.latest().navigation.id,
                            aiGuideInteractionId: interactionId,
                            errorKind: "NEAR_USAGE_QUOTA",
                        });
                    }
                }

                // Finally, add a message if the user is a teacher toggling their
                // current persona.
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationForTeacherPersona(
                        conversation,
                        pathname,
                        GuideStateApi.latest().userHasToggledPersona,
                        maybeGetAsStudentPersona(
                            userAttributes,
                            pathname,
                            GuideStateApi.latest().userAsStudent,
                        ),
                    ),
                );

                if (playback.length > 0) {
                    emitGuideAudioStream({isComplete: true});
                    // Only set the audio cache if there was no error in the
                    // audio stream.
                    setAudioCache((audioCache) =>
                        new AudioCache(audioCache).set(interactionId, playback),
                    );
                    actionStatusMap.set(
                        ActionStatusStreamAction.AUDIO,
                        ActionStatusStreamStatus.SUCCESS,
                    );
                }

                // Receiving the metadata is the end. Some code needs to know
                // for things like recording additional analytics.
                onResponseComplete?.(
                    metadata,
                    mostRecentAnswer,
                    playback.length > 0,
                );
            };

            const onStopId = (stopId: string) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }
                interactionId = stopId;
                GuideStateApi.set("stopId", stopId);
            };

            // Currently setting TEXT action status is handled by text-stream
            // and text-stream-end data chunks, and AUDIO is handled by
            // audio-stream, audio-stream-error and metadata data chunks.
            // TODO consolidate as much action status change as possible to be
            // handled by this function.
            const onActionStatus = (
                action: ActionStatusStreamAction,
                status: ActionStatusStreamStatus,
            ) => {
                // This is an additional guard-rail to prevent any
                // unintentional updates while the request has been
                // aborted.
                if (signal.aborted) {
                    return;
                }

                if (status === ActionStatusStreamStatus.SUCCESS) {
                    actionStatusMap.delete(action);
                } else {
                    actionStatusMap.set(action, status);
                }

                updateConversationByInteractionId(interactionId, {
                    actionStatusMap: new Map(actionStatusMap),
                });
            };

            return {
                onAnswer,
                onAudio,
                onCommand,
                onMetadata,
                onStopId,
                onActionStatus,
            };
        },
        [
            audioCache,
            districtId,
            pathname,
            preferences,
            reactRenderSampleRate,
            selectedVoice,
            setAudioCache,
            tabVisiblityRef,
            userAttributes,
        ],
    );

    return makeHandlers;
};
